import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  useCompanies,
  useCreateCompany,
  useDeleteCompany,
  useSetCompanyActive,
  useUpdateCompany,
} from '../../hooks/useCompanies';
import { useAccounts } from '../../hooks/useAccounts';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { setActiveCompanyId, useActiveCompanyId } from '../../stores/useCompanyStore';
import { ApiError } from '../../lib/api';
import { formatAccountingDate } from '../../lib/formatters';
import type { Company } from '@accounting/shared';

export const Route = createFileRoute('/_auth/companies')({
  component: CompaniesPage,
});

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function CompaniesPage() {
  const { data: companies = [], isLoading, error } = useCompanies(true);
  const activeId = useActiveCompanyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Company | null>(null);
  const [deleting, setDeleting] = useState<Company | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const archivedCount = companies.filter((c) => !c.isActive).length;
  const visible = showArchived ? companies : companies.filter((c) => c.isActive);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h1 style={{ fontSize: 24, margin: 0 }}>Companies</h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {archivedCount > 0 && (
            <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }} className="muted">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived ({archivedCount})
            </label>
          )}
          {!showForm && !editing && (
            <button className="btn-primary" onClick={() => setShowForm(true)}>
              New company
            </button>
          )}
        </div>
      </div>

      {showForm && <CompanyForm onClose={() => setShowForm(false)} />}
      {editing && <CompanyForm existing={editing} onClose={() => setEditing(null)} />}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load companies.</p>}

      {!isLoading && companies.length === 0 && !showForm && (
        <div className="card" style={{ marginTop: 16, textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>
            No companies yet. Create your first one to get started.
          </p>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            Create your first company
          </button>
        </div>
      )}

      {visible.length > 0 && (
        <table className="table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Legal name</th>
              <th>Fiscal year start</th>
              <th>Currency</th>
              <th>Consolidated</th>
              <th>Period locked through</th>
              <th>Processor fee account</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.id} style={{ opacity: c.isActive ? 1 : 0.6 }}>
                <td style={{ fontWeight: 500 }}>
                  {c.name}
                  {!c.isActive && <span className="tag" style={{ marginLeft: 8 }}>archived</span>}
                </td>
                <td>{c.legalName ?? '—'}</td>
                <td>{MONTHS[c.fiscalYearStartMonth - 1]}</td>
                <td>{c.currencyCode}</td>
                <td>
                  {c.isActive ? <ConsolidatedToggle company={c} /> : <span className="muted">—</span>}
                </td>
                <td>
                  <PeriodLockCell company={c} />
                </td>
                <td>
                  <ProcessorFeeAccountCell company={c} />
                </td>
                <td>{formatAccountingDate(c.createdAt.slice(0, 10))}</td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn-secondary" style={{ marginRight: 8 }} onClick={() => setEditing(c)}>
                    Edit
                  </button>
                  {c.id === activeId ? (
                    <span className="tag ok" style={{ marginRight: 8 }}>Active</span>
                  ) : (
                    <button
                      className="btn-secondary"
                      style={{ marginRight: 8 }}
                      onClick={() => setActiveCompanyId(c.id)}
                    >
                      {c.isActive ? 'Set active' : 'View data'}
                    </button>
                  )}
                  <ArchiveButton company={c} />
                  <button
                    className="btn-danger"
                    style={{ marginLeft: 8 }}
                    onClick={() => setDeleting(c)}
                    title="Permanently delete this company and all its data"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleting && (
        <DeleteCompanyModal
          company={deleting}
          companies={companies}
          activeId={activeId}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function DeleteCompanyModal({
  company,
  companies,
  activeId,
  onClose,
}: {
  company: Company;
  companies: Company[];
  activeId: string | null;
  onClose: () => void;
}) {
  const del = useDeleteCompany();
  const [typed, setTyped] = useState('');
  const matches = typed === company.name;

  const errorMessage =
    del.error instanceof ApiError
      ? (del.error.body as { error?: string } | null)?.error ?? 'Delete failed'
      : del.error?.message;

  const onConfirm = () => {
    if (!matches) return;
    del.mutate(
      { companyId: company.id, confirm: company.name },
      {
        onSuccess: () => {
          // If we just deleted the active company, move to another one so the rest
          // of the app isn't pointing at a company that no longer exists.
          if (company.id === activeId) {
            const next = companies.find((c) => c.id !== company.id && c.isActive);
            setActiveCompanyId(next ? next.id : null);
          }
          onClose();
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: '100%' }}
      >
        <h2 style={{ marginTop: 0, fontSize: 18, color: 'var(--danger, #c23b22)' }}>
          Permanently delete this company?
        </h2>
        <p style={{ marginTop: 0 }}>
          This deletes <strong>{company.name}</strong> and <strong>everything in it</strong>:
          chart of accounts, journal entries, bank accounts &amp; transactions, invoices,
          bills, payroll, imports, and attachments. This <strong>cannot be undone</strong>.
        </p>
        <p className="muted" style={{ fontSize: 13 }}>
          If you only want it out of the active list and consolidated reports, use{' '}
          <strong>Archive</strong> instead — that keeps the data and is reversible.
        </p>
        <label htmlFor="confirm-name" style={{ display: 'block', fontSize: 14, marginBottom: 6 }}>
          Type <strong>{company.name}</strong> to confirm:
        </label>
        <input
          id="confirm-name"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          autoComplete="off"
          style={{ width: '100%' }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matches && !del.isPending) onConfirm();
          }}
        />

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={del.isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={onConfirm}
            disabled={!matches || del.isPending}
          >
            {del.isPending ? 'Deleting…' : 'Permanently delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ArchiveButton({ company }: { company: Company }) {
  const setActive = useSetCompanyActive();
  if (company.isActive) {
    return (
      <button
        className="btn-danger"
        disabled={setActive.isPending}
        onClick={() => {
          if (
            confirm(
              `Archive "${company.name}"? It will be excluded from consolidated reports and the Command Center, but its data stays accessible and you can reactivate it later.`,
            )
          ) {
            setActive.mutate({ companyId: company.id, isActive: false });
          }
        }}
      >
        Archive
      </button>
    );
  }
  return (
    <button
      className="btn-secondary"
      disabled={setActive.isPending}
      onClick={() => setActive.mutate({ companyId: company.id, isActive: true })}
    >
      Reactivate
    </button>
  );
}

function ConsolidatedToggle({ company }: { company: Company }) {
  const update = useUpdateCompany();
  return (
    <label
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
      title="Include this company in the consolidated reports"
    >
      <input
        type="checkbox"
        checked={company.includeInConsolidated}
        disabled={update.isPending}
        onChange={(e) =>
          update.mutate({
            companyId: company.id,
            input: { includeInConsolidated: e.target.checked },
          })
        }
      />
      <span className="muted" style={{ fontSize: 12 }}>
        {company.includeInConsolidated ? 'Included' : 'Excluded'}
      </span>
    </label>
  );
}

/**
 * Where payment-processor fees post for this company. Set once here and the bank
 * match drawer pre-selects it, so clearing a net deposit against a gross
 * receivable doesn't mean hunting for the fee account every time.
 */
function ProcessorFeeAccountCell({ company }: { company: Company }) {
  const update = useUpdateCompany();
  const { data: accounts = [] } = useAccounts(company.id);
  const [editing, setEditing] = useState(false);
  const current = accounts.find((a) => a.id === company.processorFeeAccountId);

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        {company.processorFeeAccountId ? (
          <span>{current ? `${current.code} — ${current.name}` : 'set'}</span>
        ) : (
          <span className="muted">not set</span>
        )}
        <button
          className="btn-secondary"
          style={{ padding: '4px 8px', fontSize: 12 }}
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
      </span>
    );
  }

  const save = (value: string | null) =>
    update.mutate(
      { companyId: company.id, input: { processorFeeAccountId: value } },
      { onSuccess: () => setEditing(false) },
    );

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <div style={{ minWidth: 240 }}>
        <AccountPicker
          accounts={accounts}
          value={company.processorFeeAccountId ?? ''}
          onChange={(id) => save(id || null)}
          predicate={(a) => a.isActive && a.allowDirectPosting}
          placeholder="— select fee account —"
          disabled={update.isPending}
        />
      </div>
      {company.processorFeeAccountId && (
        <button
          className="btn-danger"
          style={{ padding: '4px 10px', fontSize: 12 }}
          disabled={update.isPending}
          onClick={() => save(null)}
          title="Clear the default"
        >
          Clear
        </button>
      )}
      <button
        className="btn-secondary"
        style={{ padding: '4px 10px', fontSize: 12 }}
        onClick={() => setEditing(false)}
      >
        Cancel
      </button>
    </span>
  );
}

function PeriodLockCell({ company }: { company: Company }) {
  const update = useUpdateCompany();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(company.periodLockedThrough ?? '');

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        {company.periodLockedThrough ? (
          <span className="tag warn">
            {formatAccountingDate(company.periodLockedThrough)}
          </span>
        ) : (
          <span className="muted">unlocked</span>
        )}
        <button
          className="btn-secondary"
          style={{ padding: '4px 8px', fontSize: 12 }}
          onClick={() => {
            setDraft(company.periodLockedThrough ?? '');
            setEditing(true);
          }}
        >
          Edit
        </button>
      </span>
    );
  }

  const save = (value: string | null) => {
    update.mutate(
      { companyId: company.id, input: { periodLockedThrough: value } },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        style={{ padding: '4px 6px' }}
      />
      <button
        className="btn-primary"
        style={{ padding: '4px 10px', fontSize: 12 }}
        disabled={update.isPending}
        onClick={() => save(draft || null)}
      >
        Save
      </button>
      {company.periodLockedThrough && (
        <button
          className="btn-danger"
          style={{ padding: '4px 10px', fontSize: 12 }}
          disabled={update.isPending}
          onClick={() => save(null)}
          title="Clear lock"
        >
          Clear
        </button>
      )}
      <button
        className="btn-secondary"
        style={{ padding: '4px 10px', fontSize: 12 }}
        onClick={() => setEditing(false)}
      >
        Cancel
      </button>
    </span>
  );
}

function CompanyForm({ existing, onClose }: { existing?: Company; onClose: () => void }) {
  const create = useCreateCompany();
  const update = useUpdateCompany();
  const isEditing = !!existing;
  const mutation = isEditing ? update : create;

  const [form, setForm] = useState({
    name: existing?.name ?? '',
    legalName: existing?.legalName ?? '',
    fiscalYearStartMonth: existing?.fiscalYearStartMonth ?? 1,
    currencyCode: existing?.currencyCode ?? 'USD',
    accountingBasis: (existing?.accountingBasis ?? 'accrual') as 'accrual' | 'cash',
    periodLockedThrough: existing?.periodLockedThrough ?? '',
    addressLine1: existing?.addressLine1 ?? '',
    addressLine2: existing?.addressLine2 ?? '',
    city: existing?.city ?? '',
    region: existing?.region ?? '',
    postalCode: existing?.postalCode ?? '',
    phone: existing?.phone ?? '',
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [key]: value }));
  const [cloneCoa, setCloneCoa] = useState(true);

  const errorMessage =
    mutation.error instanceof ApiError
      ? (mutation.error.body as { error?: string } | null)?.error ?? 'Request failed'
      : mutation.error?.message;

  const buildInput = () => ({
    name: form.name.trim(),
    legalName: form.legalName.trim() || undefined,
    fiscalYearStartMonth: form.fiscalYearStartMonth,
    currencyCode: form.currencyCode,
    accountingBasis: form.accountingBasis,
    // Empty string from the date input clears the lock (sends null); a value sets it.
    periodLockedThrough: form.periodLockedThrough ? form.periodLockedThrough : null,
    addressLine1: form.addressLine1.trim() || undefined,
    addressLine2: form.addressLine2.trim() || undefined,
    city: form.city.trim() || undefined,
    region: form.region.trim() || undefined,
    postalCode: form.postalCode.trim() || undefined,
    phone: form.phone.trim() || undefined,
  });

  return (
    <form
      className="card"
      style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (isEditing) {
          update.mutate({ companyId: existing.id, input: buildInput() }, { onSuccess: () => onClose() });
        } else {
          create.mutate({ ...buildInput(), cloneCoaTemplate: cloneCoa }, { onSuccess: () => onClose() });
        }
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: 18 }}>{isEditing ? `Edit ${existing.name}` : 'New company'}</h2>
      <div className="form-grid">
        <label htmlFor="name">Name</label>
        <input id="name" value={form.name} onChange={(e) => set('name', e.target.value)} required autoFocus />

        <label htmlFor="legal">Legal name</label>
        <input id="legal" value={form.legalName} onChange={(e) => set('legalName', e.target.value)} placeholder="(optional)" />

        <label htmlFor="fy">Fiscal year start</label>
        <select id="fy" value={form.fiscalYearStartMonth} onChange={(e) => set('fiscalYearStartMonth', Number(e.target.value))}>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>

        <label htmlFor="ccy">Currency</label>
        <select id="ccy" value={form.currencyCode} onChange={(e) => set('currencyCode', e.target.value)}>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="GBP">GBP</option>
          <option value="CAD">CAD</option>
        </select>

        <label htmlFor="basis">Accounting basis</label>
        <select id="basis" value={form.accountingBasis} onChange={(e) => set('accountingBasis', e.target.value as 'accrual' | 'cash')}>
          <option value="accrual">Accrual</option>
          <option value="cash">Cash</option>
        </select>

        {isEditing && (
          <>
            <label htmlFor="lock">Period locked through</label>
            <div>
              <input
                id="lock"
                type="date"
                value={form.periodLockedThrough}
                onChange={(e) => set('periodLockedThrough', e.target.value)}
                style={{ maxWidth: 200 }}
              />
              <button
                type="button"
                className="btn-secondary"
                style={{ marginLeft: 6, padding: '4px 10px', fontSize: 12 }}
                onClick={() => set('periodLockedThrough', '')}
                disabled={!form.periodLockedThrough}
              >
                Clear lock
              </button>
              <div className="muted" style={{ fontSize: 11, marginTop: 4, maxWidth: 460 }}>
                Refuses any journal-entry create, edit, or void with a date on or before
                this date. Use after reconciling a period to freeze it against accidental
                back-dating. Voiding a locked entry requires unlocking first.
              </div>
            </div>
          </>
        )}

        <label htmlFor="addr1">Address line 1</label>
        <input id="addr1" value={form.addressLine1} onChange={(e) => set('addressLine1', e.target.value)} placeholder="Prints on checks" />

        <label htmlFor="addr2">Address line 2</label>
        <input id="addr2" value={form.addressLine2} onChange={(e) => set('addressLine2', e.target.value)} />

        <label htmlFor="city">City</label>
        <input id="city" value={form.city} onChange={(e) => set('city', e.target.value)} />

        <label htmlFor="region">State / region</label>
        <input id="region" value={form.region} onChange={(e) => set('region', e.target.value)} maxLength={100} style={{ maxWidth: 200 }} />

        <label htmlFor="postal">Postal code</label>
        <input id="postal" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} maxLength={20} style={{ maxWidth: 200 }} />

        <label htmlFor="phone">Phone</label>
        <input id="phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} maxLength={50} />
      </div>

      {!isEditing && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            border: '1px solid var(--border)',
            borderRadius: 8,
          }}
        >
          <label style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={cloneCoa} onChange={(e) => setCloneCoa(e.target.checked)} />
            Use the default Chart of Accounts
          </label>
          <p className="muted" style={{ marginTop: 6, marginBottom: 0, fontSize: 13 }}>
            {cloneCoa
              ? 'Creates a ready-made 58-account chart (incl. payroll). Uncheck if you plan to import your own — the company starts blank except for the required system accounts.'
              : 'The company starts with only the required system accounts (A/R, A/P, Retained Earnings, Undeposited Funds, Opening Balance Equity). Import or add the rest from the Accounts page.'}
          </p>
        </div>
      )}

      {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button type="submit" className="btn-primary" disabled={mutation.isPending || !form.name.trim()}>
          {mutation.isPending ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save changes' : 'Create company'}
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
